/* eslint-disable @typescript-eslint/no-empty-object-type */
import { z } from 'zod';
import {
  ApplicantOfferRequirementTaskLevel,
  ApplicantOfferRequirementTaskStatus,
  ApplicantOfferResponseStatus,
  ApplicantOfferStatus,
  ApplicantStatusReason,
  EntryPoint,
  ScholarshipType,
} from './enums.js';
import { IApplicantOfferRequirementTask } from './requirements.js';

export const UpdateApplicantOfferStatusSchema = z
  .object({
    status: z.nativeEnum(ApplicantOfferStatus),
    conditions: z.array(z.string()).optional(),
    assess: z.array(z.string()).optional(),
    startText: z.string().optional(),
    endText: z.string().optional(),
    statusReason: z.nativeEnum(ApplicantStatusReason).optional(),
    statusInternalNote: z.string().optional(),
    statusExplanationToApplicant: z.string().optional(),
    notifyApplicant: z.boolean().optional(),
  })
  .superRefine((data, ctx) => {
    const { status, conditions, statusReason } = data;

    const statusesRequiresConditions: ApplicantOfferStatus[] = [
      ApplicantOfferStatus.Conditional,
      ApplicantOfferStatus.Unconditional,
    ];
    if (statusesRequiresConditions.includes(status) && conditions === undefined) {
      ctx.addIssue({
        code: 'custom',
        path: ['conditions'],
        message: `Status ${status} requires conditions`,
      });
    }
    if ([ApplicantOfferStatus.Withdrawn, ApplicantOfferStatus.Rejected].includes(status) && !statusReason) {
      ctx.addIssue({
        code: 'custom',
        path: ['statusReason'],
        message: `Status ${status} requires status reason`,
      });
    }
    return true;
  });

export interface IUpdateApplicantOfferStatus extends z.infer<typeof UpdateApplicantOfferStatusSchema> {}

export const UpdateApplicantOfferLetterTextSchema = z
  .object({
    startText: z.string().optional(),
    endText: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    const { startText, endText } = data;

    if (!startText && !endText) {
      ctx.addIssue({
        code: 'custom',
        path: ['startText', 'endText'],
        message: `start text or end text is required`,
      });
    }
    return true;
  });

export interface IUpdateApplicantOfferLetterText extends z.infer<typeof UpdateApplicantOfferLetterTextSchema> {}

export const UpdateApplicantOfferRequirementTaskStatusSchema = z
  .object({
    status: z.nativeEnum(ApplicantOfferRequirementTaskStatus),
    noteText: z.string().nullish(),
    conditionText: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    const { status, noteText, conditionText } = data;

    if (status === ApplicantOfferRequirementTaskStatus.Condition && !conditionText) {
      ctx.addIssue({
        code: 'custom',
        path: ['conditionText'],
        message: `ConditionText required when updating to status ${status}`,
      });
    }
    const statusesRequiresNote: ApplicantOfferRequirementTaskStatus[] = [];
    if (statusesRequiresNote.includes(status) && !noteText) {
      ctx.addIssue({
        code: 'custom',
        path: ['noteText'],
        message: `Reason required when updating to status ${status}`,
      });
    }
    return true;
  });

export function enhanceUpdateApplicantOfferRequirementTaskStatusSchema(
  previousRequirement: IApplicantOfferRequirementTask,
  offerNeedsReissue: boolean,
) {
  return UpdateApplicantOfferRequirementTaskStatusSchema.superRefine((data, ctx) => {
    const updateCondition = data.status === ApplicantOfferRequirementTaskStatus.Condition;
    // If the issue does not need reissuing, we don't need any note text
    if (offerNeedsReissue && updateCondition) {
      const previousWasCondition = previousRequirement.status === ApplicantOfferRequirementTaskStatus.Condition;
      if (previousWasCondition && !data.noteText) {
        ctx.addIssue({
          code: 'custom',
          path: ['noteText'],
          message: 'Note text must be specified when resetting conditon',
        });
      }
    }
  });
}

export interface IUpdateApplicantOfferRequirementTaskStatus extends z.infer<
  typeof UpdateApplicantOfferRequirementTaskStatusSchema
> {}

export const UpdateApplicantOfferRequirementTaskLevelSchema = z
  .object({
    level: z.nativeEnum(ApplicantOfferRequirementTaskLevel),
  })
  .refine(data => data.level !== ApplicantOfferRequirementTaskLevel.Off, {
    message: 'Level cannot be set to Off',
    path: ['level'],
  });

export interface IUpdateApplicantOfferRequirementTaskLevel extends z.infer<
  typeof UpdateApplicantOfferRequirementTaskLevelSchema
> {}

export const ExemptApplicantOfferRequirementSchema = z.object({
  noteText: z.string().nullish(),
});

export interface IExemptApplicantOfferRequirement extends z.infer<typeof ExemptApplicantOfferRequirementSchema> {}

const FixedScholarshipSchema = z.object({
  name: z.string(),
  type: z.literal(ScholarshipType.FIXED),
  amount: z.number(),
});

const PercentageScholarshipSchema = z.object({
  name: z.string(),
  type: z.literal(ScholarshipType.PERCENTAGE),
  percentage: z.number(),
});

const ScholarshipSchema = z.discriminatedUnion('type', [FixedScholarshipSchema, PercentageScholarshipSchema]);

// TODO: This is duplicated from IApplicantOffer['programme'] which has types from IApplyCourse. How do we do this?
export const UpdateApplicantOfferProgrammeDetailsSchema = z.object({
  entryPoint: z.nativeEnum(EntryPoint),
  startDate: z.string().date(),
  endDate: z.string().date(),
  fee: z.number(),
  feeTotal: z.number().optional(),
  scholarships: z.array(ScholarshipSchema).optional(),
  campus: z.string().optional(),
});

export interface IUpdateApplicantOfferProgrammeDetails extends z.infer<
  typeof UpdateApplicantOfferProgrammeDetailsSchema
> {}

export const StaffCreateApplicantOfferSchema = UpdateApplicantOfferProgrammeDetailsSchema.extend({
  intakeId: z.string(),
  courseId: z.string(),
});

export interface IStaffCreateApplicantOffer extends z.infer<typeof StaffCreateApplicantOfferSchema> {}

export const UpdateApplicantOfferStatusActionSchema = z.object({
  applicantStatus: z.nativeEnum(ApplicantOfferResponseStatus),
});

export interface ApplicantOfferStatusQueryParams {
  offerId: string;
}

export interface IApplicantOfferStatusAction extends z.infer<typeof UpdateApplicantOfferStatusActionSchema> {}

export interface UpdateApplicantOfferStatusQueryParams extends ApplicantOfferStatusQueryParams {}
export const CreateApplicantOfferRequirementTaskSchema = z
  .object({
    requirementId: z.string(),
    requirementText: z.string().optional(),
    conditionText: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    const isCustom = !!data.requirementText;
    if (isCustom && (!data.conditionText || !data.conditionText.trim())) {
      ctx.addIssue({
        code: 'custom',
        path: ['conditionText'],
        message: 'conditionText is required for custom requirements',
      });
    }
  });

export const CreateApplicantOfferRequirementTasksSchema = z.object({
  requirements: z.array(CreateApplicantOfferRequirementTaskSchema),
});

export interface ICreateApplicantOfferRequirementTasks extends z.infer<
  typeof CreateApplicantOfferRequirementTasksSchema
> {}

export const ReinstateApplicantOfferSchema = z.object({});

export interface IReinstateApplicantOffer extends z.infer<typeof ReinstateApplicantOfferSchema> {}
